import React, { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LeftNavIconButton } from './LeftNavIconButton'
import { useLeftNavStore } from './left-nav-store'
import { useAuth } from 'context/authentication/useAuth'
import { featureFlagService } from 'utils/featureFlagService'
import { Z_INDEX } from 'styles/z-index'
import type { NavItem, NavSection, NavIconType } from './types'

// Define navigation items with their configurations
const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'house',
    path: '/home',
    hasSlideOut: false,
  },
  {
    id: 'targets',
    label: 'Targets',
    icon: 'target',
    path: '/targets',
    hasSlideOut: true,
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: 'megaphone',
    path: '/campaigns',
    hasSlideOut: true,
  },
  {
    id: 'samples',
    label: 'Samples',
    icon: 'hand-coins',
    path: '/samples',
    hasSlideOut: false,
  },
  {
    id: 'rebates',
    label: 'Rebates',
    icon: 'seal-percent',
    path: '/rebates',
    hasSlideOut: false,
  },
  {
    id: 'deals',
    label: 'Deals',
    icon: 'handshake',
    path: '/deals',
    hasSlideOut: false,
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: 'chart-line-up',
    path: '/dashboard',
    hasSlideOut: true,
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: 'workspace',
    path: '/documents',
    hasSlideOut: true,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'gear',
    path: '/settings',
    hasSlideOut: true,
  },
]

export const LeftNavBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const featureFlags = featureFlagService()

  const {
    isCollapsed,
    activeSection,
    isSlideOutOpen,
    setActiveSection,
    openSlideOut,
    closeSlideOut,
    lastVisited,
    setLastVisited,
  } = useLeftNavStore()

  // Filter nav items based on feature flags
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => {
      if (item.id === 'home' && !featureFlags.enableHomepage) return false
      if (item.id === 'samples' && !featureFlags.enableSamples) return false
      if (item.id === 'campaigns' && !featureFlags.enableCampaigns) return false
      return true
    })
  }, [featureFlags])

  // Determine active section from current path
  const currentActiveSection = useMemo((): NavSection | null => {
    const path = location.pathname

    if (
      path.startsWith('/documents') ||
      path.startsWith('/contacts') ||
      path.startsWith('/activity') ||
      path.startsWith('/products')
    ) {
      return 'workspace'
    }

    // Check each nav item to see if current path matches
    for (const item of NAV_ITEMS) {
      if (path.startsWith(item.path)) {
        return item.id
      }
    }

    return null
  }, [location.pathname])

  /**
   * Navigation State Management (FIR-4955)
   *
   * Automatically tracks the user's last visited page within each navigation section.
   * This enables "memory" behavior where returning to a section restores the previous
   * page instead of resetting to the default.
   *
   * Example:
   * - User navigates to Targets > Commercial > Hotels
   * - User switches to Deals section
   * - User clicks Targets again → returns to Commercial > Hotels (not default Restaurants & Bars)
   *
   * State is persisted to localStorage via Zustand middleware for cross-session memory.
   */
  React.useEffect(() => {
    if (currentActiveSection) {
      setLastVisited(currentActiveSection, location.pathname)
    }
  }, [currentActiveSection, location.pathname, setLastVisited])

  /**
   * Handles navigation item clicks with intelligent routing (FIR-4955)
   *
   * Behavior:
   * 1. For sections WITH slide-out panels (Targets, Campaigns, Performance, Workspace, Settings):
   *    - First click: Opens panel AND navigates to last visited or default page
   *    - Subsequent clicks while open: Toggles panel closed
   *
   * 2. For sections WITHOUT slide-out panels (Samples, Rebates, Deals):
   *    - Always navigates to last visited or default page
   *    - Closes any open panels
   *
   * Navigation Priority:
   * - lastVisited[section] (user's previous page in this section)
   * - item.path (default page defined in NAV_ITEMS)
   */
  const handleNavClick = useCallback(
    (item: NavItem) => {
      const isCurrentlyActive = currentActiveSection === item.id

      if (item.hasSlideOut) {
        // For sections with slide-out panels
        if (isCurrentlyActive && isSlideOutOpen) {
          // Close slide-out if clicking the already active section with panel open
          closeSlideOut()
        } else {
          // Open slide-out AND navigate to last visited or default page
          openSlideOut(item.id)
          navigate(lastVisited[item.id] || item.path)
        }
      } else {
        // For sections without slide-out panels, navigate directly
        setActiveSection(item.id)
        closeSlideOut()
        navigate(lastVisited[item.id] || item.path)
      }
    },
    [
      currentActiveSection,
      isSlideOutOpen,
      setActiveSection,
      openSlideOut,
      closeSlideOut,
      navigate,
      lastVisited,
    ]
  )

  const handleHelpClick = useCallback(() => {
    window.open('https://help.firstbite.io/', '_blank')
  }, [])

  const handleLogoutClick = useCallback(() => {
    logout()
  }, [logout])

  return (
    <nav
      className={`${isCollapsed ? 'hidden' : 'flex'} flex-col w-[var(--left-nav-width)] min-w-[var(--left-nav-width)] h-full bg-[var(--nav-bg-primary)] px-3 pb-6`}
      style={{ zIndex: Z_INDEX.LEFT_NAV, width: 'var(--left-nav-width)' }}
      aria-label="Main navigation"
      data-left-nav
    >
      <div className="flex flex-col flex-1 justify-between items-center min-h-0">
        {/* Top Section */}
        <div className="flex flex-col items-center gap-[20px] pt-1">
          {visibleNavItems.map((item) => (
            <LeftNavIconButton
              key={item.id}
              icon={item.icon as NavIconType}
              label={item.label}
              isSelected={currentActiveSection === item.id}
              isSecondaryActive={
                item.hasSlideOut && activeSection === item.id && isSlideOutOpen
              }
              onClick={() => handleNavClick(item)}
              showLabel={true}
            />
          ))}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col items-center gap-3">
          <LeftNavIconButton
            icon="question"
            label="Help Center"
            onClick={handleHelpClick}
          />
          {/* Divider */}
          <div className="w-11 h-px bg-[var(--nav-text-primary)] opacity-50 my-2" />
          <LeftNavIconButton
            icon="sign-out"
            label="Log Out"
            onClick={handleLogoutClick}
          />
        </div>
      </div>
    </nav>
  )
}
